import { Badge, Button, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { AdminHtsSubmissionFilterStatus, isHtsReviewPending } from '@evaltick/shared';

import type { AdminHtsSubmissionListItem } from '@/services/adminService';
import { bandLabelFor } from '@/pages/Sessions/AdminHtsSection';
import { formatUserName } from '@/utils/formatters';
import { HTS_SUBMISSION_STATUS_OPTIONS } from './resultsFilterOptions';
import type { ResultsSortOrder } from './useSortOrderState';
import styles from './AdminAssessments.module.scss';

const HTS_SUBMISSION_STATUS_LABELS: Record<AdminHtsSubmissionFilterStatus, string> = Object.fromEntries(
  HTS_SUBMISSION_STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<AdminHtsSubmissionFilterStatus, string>;

// Mirrors htsSubmissionStatusWhere (apps/api/src/services/htsAdminReview.ts) —
// same 3-way classification the Status filter uses, derived client-side from
// fields already on the row.
const getHtsSubmissionStatus = (record: AdminHtsSubmissionListItem): AdminHtsSubmissionFilterStatus => {
  if (record.releasedAt !== null) return AdminHtsSubmissionFilterStatus.Released;
  return record.reviewRequired
    ? AdminHtsSubmissionFilterStatus.NeedsReview
    : AdminHtsSubmissionFilterStatus.NoReviewNeeded;
};

export const buildHtsSubmissionColumns = (
  onView: (submissionId: string) => void,
  dateSortOrder: ResultsSortOrder,
): ColumnsType<AdminHtsSubmissionListItem> => [
  {
    title: 'User',
    key: 'user',
    width: 180,
    ellipsis: true,
    render: (_, record) => {
      // A guest has only a synthetic name/email, so the access code's tags are
      // the meaningful identifier here instead.
      if (record.accessCodeTags !== null) {
        return record.accessCodeTags.length > 0 ? (
          <>
            {record.accessCodeTags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </>
        ) : (
          <Typography.Text type="secondary">Guest</Typography.Text>
        );
      }
      return (
        formatUserName(record.user.firstName, record.user.lastName) || (
          <Typography.Text type="secondary">{record.user.email}</Typography.Text>
        )
      );
    },
  },
  {
    title: 'Email',
    key: 'email',
    width: 220,
    ellipsis: true,
    render: (_, record) =>
      record.accessCodeTags !== null ? (
        <Typography.Text type="secondary">—</Typography.Text>
      ) : (
        record.user.email
      ),
  },
  {
    title: 'Package',
    key: 'interviewTemplate',
    width: 200,
    ellipsis: true,
    render: (_, record) => record.interviewTemplateName,
  },
  {
    title: 'Status',
    key: 'status',
    width: 150,
    render: (_, record) => {
      const status = getHtsSubmissionStatus(record);
      const label = HTS_SUBMISSION_STATUS_LABELS[status];
      if (status === AdminHtsSubmissionFilterStatus.Released) return <Badge status="success" text={label} />;
      if (status === AdminHtsSubmissionFilterStatus.NeedsReview) return <Badge status="error" text={label} />;
      return <Typography.Text type="secondary">{label}</Typography.Text>;
    },
  },
  {
    title: 'Score',
    key: 'score',
    width: 170,
    render: (_, record) => {
      // The review gate alone, guest or not — a released submission keeps showing its score even if a later edit re-trips a trigger.
      if (isHtsReviewPending(record)) return <Badge status="error" text="Needs review" />;
      if (record.compositeScore === null) return <Typography.Text type="secondary">Not yet computed</Typography.Text>;
      return (
        <span className={styles.scoreCell}>
          <Typography.Text type="secondary">{record.compositeScore} / 100</Typography.Text>
          {/* releasedAt is only ever stamped for a submission that tripped a mandatory review trigger, so its presence alone means a review was signed off. */}
          {record.releasedAt && <Badge status="success" text="Released" />}
        </span>
      );
    },
  },
  {
    title: 'Readiness',
    key: 'readiness',
    width: 170,
    render: (_, record) => {
      if (isHtsReviewPending(record)) return <Typography.Text type="secondary">—</Typography.Text>;
      if (record.band === null) return <Typography.Text type="secondary">Not yet computed</Typography.Text>;
      return (
        <span className={styles.scoreCell}>
          <Tag>{bandLabelFor(record.band)}</Tag>
        </span>
      );
    },
  },
  {
    title: 'Flagged',
    key: 'flagged',
    width: 90,
    render: (_, record) =>
      record.flaggedAnswerCount > 0 ? (
        <Badge status="error" text={record.flaggedAnswerCount} />
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
  {
    title: 'Date',
    key: 'date',
    width: 130,
    sorter: (_a, _b) => 0,
    sortOrder: dateSortOrder,
    render: (_, record) =>
      record.submittedAt ? new Date(record.submittedAt).toLocaleDateString() : '—',
  },
  {
    title: '',
    key: 'actions',
    width: 135,
    fixed: 'right',
    render: (_, record) => (
      <Button type="link" size="small" onClick={() => onView(record.submissionId)}>
        View results
      </Button>
    ),
  },
];
